







import DashboardUtils, {
  DashboardTile,
  TileLikertScoreOptions,
} from '@/utils/DashboardUtils';
import { OversightOrganization } from '@/utils/OversightUtils';
import { SurveyGroup } from '@/utils/SurveyUtils';
import mixins from 'vue-typed-mixins';
import { Scatter } from 'vue-chartjs';
import { colorGradient, ColorRGB } from '@/utils/ChartUtils';
import { TileAPIParams } from '@/utils/TileUtils';

const Chart = mixins().extend({
  extends: Scatter,

  props: {
    tile: {
      type: Object as () => DashboardTile<TileLikertScoreOptions>,
    },
    data: {
      type: Array as () => {
        questionkey: string;
        netcount: number;
        answercount: number;
        score: number;
      }[],
    },
  },

  computed: {
    chartData(): any {
      return {
        labels: [],
        datasets: this.data?.map((item) => {
          const questionIndex =
            this.tile.options.questionkeys.length -
            this.tile.options.questionkeys?.findIndex(
              (questionkey) => questionkey.key === item.questionkey
            );

          const score =
            item.score / item[this.tile.options.denominator || 'answercount'];

          const colour = colorGradient(
            score / this.maxx,
            this.tile.options.gradients.start,
            this.tile.options.gradients.middle,
            this.tile.options.gradients.end
          );

          return {
            data: [
              {
                x: -1,
                y: questionIndex,
              },
              {
                x: score,
                y: questionIndex,
              },
            ],
            fill: false,
            showLine: true,
            pointRadius: 8,
            pointHoverRadius: 8,
            // borderColor: colour,
            pointBackgroundColor: colour,
          };
        }),
      };
    },

    chartOptions(): any {
      return {
        title: {
          display: false,
        },
        legend: {
          display: false,
        },
        tooltips: {
          enabled: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                drawBorder: false,
                tickMarkLength: 20,
              },
              scaleLabel: {
                display: !!this.$translate(this.tile.options?.scorelabel),
                labelString: this.$translate(this.tile.options?.scorelabel),
              },
              ticks: {
                stepSize: 1,
                max: this.maxx,
                min: this.minx,
                padding: 10,
                callback: (value: any, index: any, values: any) => {
                  return this.$translate(
                    this.tile.options?.scorekeys.find(
                      (scorekey) => scorekey.score === value
                    )?.label
                  );
                },
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                display: false,
              },
              ticks: {
                stepSize: 1,
                max: this.tile.options?.questionkeys?.length,
                min: 1,
                padding: 10,
                callback: (value: any, index: any, values: any) => {
                  return this.$translate(
                    this.tile.options?.questionkeys[index]?.label
                  );
                },
              },
            },
          ],
        },
        responsive: true,
        maintainAspectRatio: false,
      };
    },

    minx(): number {
      let min = undefined;
      for (const i in this.tile.options?.scorekeys) {
        const score = this.tile.options?.scorekeys[i].score;
        if (min === undefined || min > score) {
          min = score;
        }
      }
      return min || 0;
    },

    maxx(): number {
      let max = 0;
      for (const i in this.tile.options?.scorekeys) {
        const score = this.tile.options?.scorekeys[i].score;
        if (max < score) {
          max = score;
        }
      }
      return max;
    },
  },

  mounted() {
    // @ts-ignore
    this.renderChart(this.chartData, this.chartOptions);
  },

  watch: {
    chartData() {
      this.reload();
    },
    chartOptions() {
      this.reload();
    },
  },

  methods: {
    reload() {
      this.$data._chart.data = this.chartData;
      this.$data._chart.options = this.chartOptions;
      this.$data._chart.update();
    },
  },
});

export default mixins().extend({
  components: {
    Chart,
  },

  props: {
    tile: {
      type: Object as () => DashboardTile<TileLikertScoreOptions>,
    },

    params: {
      type: Object as () => TileAPIParams,
    },
  },

  data(): {
    data?: any;
  } {
    return {
      data: undefined,
    };
  },

  mounted() {
    this.load();
  },

  watch: {
    params(current, previous) {
      if (
        current.surveygroup !== previous.surveygroup ||
        current.oversight !== previous.oversight
      ) {
        this.load();
      }
    },
  },

  methods: {
    async load() {
      if (this.params?.surveygroup?.id) {
        try {
          this.data = undefined;
          this.$alert.hide('load');
          this.data = await DashboardUtils.getLikertScore({
            surveygroup: this.params.surveygroup,
            oversight: this.params.oversight,
            questionkeys: this.tile.options?.questionkeys?.map(
              (questionkey) => questionkey.key
            ),
          });
        } catch (error) {
          this.$alert.show('load', {
            type: 'error',
            messages: this.$handleAPIError(error),
          });
        }
      }
    },
  },
});
